/* eslint-disable react-hooks/exhaustive-deps */
import React, {useContext, useEffect, useMemo, useState} from "react"
import {Radio, Space, Spin} from "antd"
import {useTranslation} from "react-i18next"

import {BaseButton} from "components"
import {FormLabel} from "components/Form"
import {formatSavedPaymentMethodLabel, handleError, PAYMENT_METHOD_NEW} from "helpers"
import {useRetailersGatewayCollect, useStudentPaymentMethods} from "hooks"
import {RetailersGatewayProviderConfig} from "hooks/usePaymentProviderConfigs"
import {RetailersGatewayPaymentType} from "hooks/useRetailersGatewayCollect"
import {studentPaymentService} from "services"
import styles from "./RetailersGatewayPayment.module.css"
import {GeneralPayment} from "types/student-account/ledger-accounts"
import {StudentPaymentContext} from "context/StudentPaymentContext"
import {StudentCharge} from "types/student-account/student-ledger"
import {PaymentPlanItem} from "types/student-account/payment-plan"
import {
    PaymentCreatedFromPlatform,
    PaymentProvider,
    RetailersGatewayPaymentDetails
} from "types/student-account/student-payment"

type Props = {
    providerConfig: RetailersGatewayProviderConfig
    studentId: number
    paymentAmount: number
    currency
    isCheckingOut: boolean
    selectedPayment?: GeneralPayment
    customNote: string
    payerFullName?: string
    selectedCharges?: StudentCharge[]
    selectedPaymentItems?: PaymentPlanItem[]
    academicYear?: number
}

const splitFullName = (fullName?: string) => {
    const parts = (fullName ?? "").trim().split(/\s+/).filter(Boolean)
    return {firstName: parts[0], lastName: parts.slice(1).join(" ") || undefined}
}

export const RetailersGatewayPayment = (props: Props) => {
    const {
        providerConfig,
        studentId,
        paymentAmount,
        currency,
        isCheckingOut,
        selectedPayment,
        customNote,
        payerFullName,
        selectedCharges,
        selectedPaymentItems,
        academicYear
    } = props

    const {t} = useTranslation(["payment", "common"])
    const {setPaymentStatus, setPaymentError} = useContext(StudentPaymentContext)
    const {isReady, error: collectError, openPaymentForm} = useRetailersGatewayCollect(providerConfig)

    const [studentPayment, setStudentPayment] = useState<any | undefined>()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [paymentType, setPaymentType] = useState<RetailersGatewayPaymentType>("card")

    const {paymentMethods: allPaymentMethods, isLoading: isLoadingPaymentMethods} = useStudentPaymentMethods({
        profileId: studentId
    })
    const paymentMethods = useMemo(
        () => allPaymentMethods.filter((method) => method.provider === PaymentProvider.RetailersGateway),
        [allPaymentMethods]
    )
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<number | undefined>()

    // Pre-select the active saved method (or the first one); "add new" when none is on file.
    useEffect(() => {
        // Wait for the saved methods to load, so the active one is pre-selected rather than
        // "add new" being locked in from the initial empty list.
        if (isLoadingPaymentMethods) return
        setSelectedPaymentMethodId((prev) => {
            if (prev !== undefined) return prev
            if (!paymentMethods.length) return PAYMENT_METHOD_NEW
            return (paymentMethods.find((method) => method.isActive) ?? paymentMethods[0]).paymentMethodId
        })
    }, [paymentMethods, isLoadingPaymentMethods])

    const isNewMethod = selectedPaymentMethodId === undefined || selectedPaymentMethodId === PAYMENT_METHOD_NEW

    useEffect(() => {
        if (collectError) handleError(collectError)
    }, [collectError])

    useEffect(() => {
        if (!studentId || !paymentAmount || !isCheckingOut) {
            setStudentPayment(undefined)
            return
        }

        ;(async function initStudentPayment() {
            try {
                const {studentPayment} = await studentPaymentService.createRetailersGatewayStudentPayment({
                    amountInCents: Math.round(paymentAmount * 100),
                    currency,
                    studentProfileId: studentId,
                    generalPaymentId: selectedPayment?.generalPaymentId,
                    payerFullName,
                    academicYear,
                    // One-time payment only (recurring setup goes through RecurringPaymentCheckout) —
                    // link every selected item at its own remaining amount, not the aggregate total.
                    paymentPlanItems: selectedPaymentItems?.length
                        ? selectedPaymentItems
                              .filter((item) => item.paymentAmount)
                              .map((item) => ({
                                  paymentPlanId: item.paymentPlanId,
                                  paymentPlanItemId: item.paymentPlanItemId,
                                  accountId: item.paymentAccountId,
                                  amount: item.paymentAmount
                              }))
                        : undefined,
                    notes: `${selectedPayment?.name || ""}${customNote ? `: ${customNote}` : ""}`,
                    createdFromPlatform: PaymentCreatedFromPlatform.EdularWeb
                })
                if (selectedCharges?.length) {
                    await studentPaymentService.assignPaymentCharges(
                        studentPayment.paymentId,
                        selectedCharges.map((charge) => ({chargeId: charge.chargeId, amount: charge.paymentAmount}))
                    )
                }
                setStudentPayment(studentPayment)
            } catch (error) {
                handleError(error)
            }
        })()
    }, [
        studentId,
        isCheckingOut,
        paymentAmount,
        currency,
        selectedPayment,
        customNote,
        payerFullName,
        selectedCharges,
        selectedPaymentItems
    ])

    const completePayment = async (
        paymentFields:
            | {paymentMethodId: number}
            | {paymentToken: string; paymentDetails: RetailersGatewayPaymentDetails}
    ) => {
        setIsSubmitting(true)
        setPaymentStatus("processing")
        setPaymentError(undefined)
        try {
            const {firstName, lastName} = splitFullName(payerFullName)
            const result = await studentPaymentService.completeRetailersGatewayStudentPayment({
                studentProfileId: studentPayment.studentProfileId,
                paymentId: studentPayment.paymentId,
                payerFirstName: firstName,
                payerLastName: lastName,
                ...paymentFields
            })
            setPaymentStatus("confirmed")
            if (result) {
                window.location.href = `${window.location.origin}?paymentId=${studentPayment.paymentId}`
            }
        } catch (error: any) {
            setPaymentStatus("failed")
            setPaymentError(error?.response?.data?.message || error?.message || "")
        } finally {
            setIsSubmitting(false)
        }
    }

    const handlePay = () => {
        if (!studentPayment) return
        if (!isNewMethod && selectedPaymentMethodId) {
            completePayment({paymentMethodId: selectedPaymentMethodId})
            return
        }
        try {
            // The payment is only submitted once the student completes the gateway's lightbox —
            // closing it leaves the checkout untouched.
            openPaymentForm(paymentType, (paymentToken, paymentDetails) =>
                completePayment({paymentToken, paymentDetails})
            )
        } catch (error) {
            handleError(error)
        }
    }

    if (!studentPayment) {
        return (
            <div>
                <Spin />
            </div>
        )
    }

    return (
        <div>
            {paymentMethods.length > 0 && (
                <div className={styles.payFields}>
                    <FormLabel label={t("common:label.paymentMethods", "Payment Method")} />
                    <Radio.Group
                        onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                        value={selectedPaymentMethodId}>
                        <Space direction="vertical">
                            {paymentMethods.map((method) => (
                                <Radio key={method.paymentMethodId} value={method.paymentMethodId}>
                                    {formatSavedPaymentMethodLabel(method)}
                                </Radio>
                            ))}
                            <Radio value={PAYMENT_METHOD_NEW}>
                                {t("common:label.addNewPaymentMethod", "Add new payment method")}
                            </Radio>
                        </Space>
                    </Radio.Group>
                </div>
            )}

            {isNewMethod && (
                <div className={styles.payFields}>
                    <Radio.Group value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
                        <Radio value="card">Pay by Card</Radio>
                        <Radio value="ach">Pay by Bank Account (ACH)</Radio>
                    </Radio.Group>
                </div>
            )}

            <div className={styles.doneBtn}>
                <BaseButton
                    title={isNewMethod ? "Enter Payment Details & Pay" : "Pay"}
                    isActive
                    loading={isSubmitting}
                    onClick={handlePay}
                    disabled={isSubmitting || (isNewMethod && !isReady)}
                />
            </div>
        </div>
    )
}
